import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getLabTestById } from "../../apis/LabOrder";

const ViewLabTest: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const testId = id ? parseInt(id) : 0;
  const [test, setTest] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    if (testId) {
      fetchTestDetails(testId);
    }
  }, [testId]);

  const fetchTestDetails = async (id: number) => {
    try {
      setIsFetching(true);
      const res = await getLabTestById(id);
      setTest(res.data.data);
    } catch (err) {
      console.error("Error fetching test details:", err);
    } finally {
      setIsFetching(false);
    }
  };

  if (isFetching) {
    return (
      <div className="max-w-6xl p-6">
        <div className="text-center">Loading test details...</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="max-w-6xl p-6">
        <div className="text-center text-red-600">Test not found</div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-gray-900">
          Test Details - {test.name || "-"}
        </h1>
      </div>

      {/* Test Details */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        {/* Sections and Parameters - Single Table */}
        {test?.sections && test.sections.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                   
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                    Result
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-300">
                    Unit
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Reference Range
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {test.sections.map((section: any, sectionIndex: number) => {
                  const sectionParams = section.parameters || [];
                  if (sectionParams.length === 0) return null;

                  return (
                    <React.Fragment key={section.id || sectionIndex}>
                      {/* Section Header Row */}
                      <tr className="bg-gray-100">
                        <td
                          colSpan={4}
                          className="px-4 py-2 text-sm font-semibold text-gray-900 uppercase border-b border-gray-300"
                        >
                          {section.name}
                        </td>
                      </tr>
                      {/* Parameters Rows */}
                      {sectionParams.map((param: any, paramIndex: number) => {
                        // Calculate reference range correctly
                        let referenceRange = "Absent";
                        const refText = param.reference_text || param.referenceText;
                        const refMin = param.reference_min || param.referenceMin;
                        const refMax = param.reference_max || param.referenceMax;
                        
                        if (refText) {
                          referenceRange = refText;
                        } else if (
                          refMin !== null &&
                          refMin !== undefined &&
                          refMin !== "" &&
                          refMax !== null &&
                          refMax !== undefined &&
                          refMax !== ""
                        ) {
                          referenceRange = `${refMin} - ${refMax}`;
                        } else if (
                          refMin !== null &&
                          refMin !== undefined &&
                          refMin !== ""
                        ) {
                          referenceRange = `≥ ${refMin}`;
                        } else if (
                          refMax !== null &&
                          refMax !== undefined &&
                          refMax !== ""
                        ) {
                          referenceRange = `≤ ${refMax}`;
                        }

                        return (
                          <tr
                            key={param.id || `${section.id}-${paramIndex}`}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300 font-medium capitalize">
                              {param.name || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-300">
                              -
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-300">
                              {param.unit || "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {referenceRange}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No sections found for this test
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
        <button
          onClick={() => navigate("/test-list")}
          type="button"
          className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default ViewLabTest;
